import { jsPDF } from 'jspdf';
import { SignContractPayload } from 'services/contractSign';

const PAGE_MARGIN = 18;
const PAGE_WIDTH = 210;
const CONTENT_WIDTH = PAGE_WIDTH - PAGE_MARGIN * 2;
const PAGE_BOTTOM = 278;

const safeFilePart = (value: string) =>
  value
    .trim()
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase() || 'contract';

const loadLogo = async (url: string): Promise<{ data: string; width: number; height: number }> => {
  const response = await fetch(url);
  if (!response.ok) throw new Error('Unable to load company logo.');

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('Unsupported company logo image.'));
      element.src = objectUrl;
    });
    const canvas = document.createElement('canvas');
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Unable to process company logo.');
    context.drawImage(image, 0, 0);

    return {
      data: canvas.toDataURL('image/png'),
      width: image.naturalWidth,
      height: image.naturalHeight,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export const generateContractPdf = async (contract: SignContractPayload): Promise<void> => {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = 20;

  const ensureSpace = (height: number) => {
    if (y + height > PAGE_BOTTOM) {
      pdf.addPage();
      y = 20;
    }
  };

  const writeLines = (
    text: string,
    options: { fontSize?: number; bold?: boolean; gapAfter?: number } = {}
  ) => {
    const fontSize = options.fontSize ?? 10;
    const lineHeight = fontSize * 0.42;
    pdf.setFont('helvetica', options.bold ? 'bold' : 'normal');
    pdf.setFontSize(fontSize);

    const paragraphs = text.replace(/\r\n/g, '\n').split('\n');
    paragraphs.forEach((paragraph) => {
      const lines = paragraph ? pdf.splitTextToSize(paragraph, CONTENT_WIDTH) : [''];
      lines.forEach((line: string) => {
        ensureSpace(lineHeight);
        pdf.text(line, PAGE_MARGIN, y);
        y += lineHeight;
      });
    });
    y += options.gapAfter ?? 2;
  };

  pdf.setProperties({
    title: `Magazine advertising contract - ${contract.company_name}`,
    subject: 'Magazine advertising contract',
    author: contract.contract_from_name || 'Torch Marketing Co Ltd',
  });

  if (contract.company_logo) {
    try {
      const logo = await loadLogo(`${process.env.REACT_APP_BACKEND_URL}/${contract.company_logo}`);
      const maxWidth = 52;
      const maxHeight = 24;
      const scale = Math.min(maxWidth / logo.width, maxHeight / logo.height);
      const width = logo.width * scale;
      const height = logo.height * scale;
      pdf.addImage(logo.data, 'PNG', PAGE_MARGIN, y, width, height);
      y += height + 6;
    } catch {
      // The contract remains downloadable if the remote logo is unavailable.
    }
  }

  writeLines('MAGAZINE ADVERTISING CONTRACT', { fontSize: 18, bold: true, gapAfter: 4 });
  writeLines(`Contract from: ${contract.contract_from_name || 'Torch Marketing Co Ltd'}`, { bold: true, gapAfter: 1 });
  writeLines(`Company: ${contract.company_name || '-'}`, { bold: true, gapAfter: 1 });
  writeLines(`Contract reference: ${contract.id}`, { gapAfter: 1 });
  writeLines(`Status: ${contract.status.toUpperCase()}`, { gapAfter: 5 });
  if (contract.purchase_order_number) {
    writeLines(`PO number: ${contract.purchase_order_number}`, { gapAfter: 1 });
  }
  if (contract.vat_number) {
    writeLines(`VAT number: ${contract.vat_number}`, { gapAfter: 5 });
  }

  if (contract.proposal?.line_items?.length) {
    writeLines('ORDER SUMMARY', { fontSize: 13, bold: true, gapAfter: 3 });

    contract.proposal.line_items.forEach((item, index) => {
      const description = [item.publication, item.issue, item.label, item.size]
        .filter(Boolean)
        .join(' | ');
      writeLines(`${index + 1}. ${description}`, { bold: true, gapAfter: 0.5 });
      writeLines(`Price: ${item.price_formatted}`, { gapAfter: 2 });
    });

    writeLines(`Total: ${contract.proposal.total_price_formatted}`, {
      fontSize: 12,
      bold: true,
      gapAfter: 6,
    });
  }

  writeLines('TERMS', { fontSize: 13, bold: true, gapAfter: 3 });
  writeLines(contract.body || 'No contract terms were supplied.', { gapAfter: 7 });

  if (contract.terms_and_conditions) {
    writeLines('TERMS & CONDITIONS', { fontSize: 13, bold: true, gapAfter: 3 });
    writeLines(contract.terms_and_conditions, { gapAfter: 7 });
  }

  writeLines('SIGNATURE', { fontSize: 13, bold: true, gapAfter: 3 });
  if (contract.status === 'signed') {
    writeLines(`Electronically signed by: ${contract.signer_name || '-'}`, { bold: true, gapAfter: 1 });
    if (contract.signer_email) writeLines(`Email: ${contract.signer_email}`, { gapAfter: 1 });
    if (contract.signed_at) {
      writeLines(`Signed: ${new Date(contract.signed_at).toLocaleString('en-GB')}`, { gapAfter: 1 });
    }
  } else {
    writeLines('Name: __________________________________________', { gapAfter: 4 });
    writeLines('Signature: _______________________________________', { gapAfter: 4 });
    writeLines('Date: ___________________________________________', { gapAfter: 1 });
  }

  const pageCount = pdf.getNumberOfPages();
  for (let page = 1; page <= pageCount; page += 1) {
    pdf.setPage(page);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(110);
    pdf.text(`Contract ${contract.id} | Page ${page} of ${pageCount}`, PAGE_MARGIN, 290);
  }

  pdf.save(`${safeFilePart(contract.company_name)}-contract-${contract.id}.pdf`);
};
